const PRODUCT_NAME  = process.env.REACT_APP_PRODUCT_NAME  ?? "Your Product";
const COMPANY_NAME  = process.env.REACT_APP_COMPANY_NAME  ?? "Your Company";
const COMPANY_ADDR  = process.env.REACT_APP_COMPANY_REGISTERED_ADDRESS ?? "Your Registered Address";
const COMPANY_NO    = process.env.REACT_APP_COMPANY_REGISTERED_NUMBER ?? "00000000";
const SUPPORT_EMAIL = process.env.REACT_APP_SUPPORT_EMAIL ?? "support@example.com";

export const TERMS_HTML_GB = `

<p><strong>Provider:</strong> ${COMPANY_NAME} (Company No. ${COMPANY_NO}), registered in England &amp; Wales, with registered office at ${COMPANY_ADDR} (“<em>We/Us</em>”).</p>
<p><strong>Customer:</strong> The business using the Services, being the entity that creates an account or is identified in any order form or subscription (“<em>You/Customer</em>”).</p>

<h4>1. The Service</h4>
<ul>
<li>We provide a SaaS booking platform known as <strong>${PRODUCT_NAME}</strong> (“<em>Services</em>”), including: staff &amp; service setup, staff schedules, calendar with single/recurring events, customer &amp; appointment records with notes, search, dashboard, booking portal and optional mini-site, transactional communications, and optional Stripe Connect payments integration.</li>
</ul>

<h4>2. Accounts &amp; Eligibility</h4>
<ul>
  <li>You must provide accurate information and keep credentials secure.</li>
  <li>You are responsible for actions taken under your account by your users.</li>
  <li>You must be authorised to bind your business to these Terms.</li>
</ul>

<h4>3. Fees &amp; Payment</h4>
<ul>
  <li>Subscriptions are billed as set out at the point of order or in-app billing. Fees are due in advance and are non-refundable except where required by law.</li>
  <li>Plans renew automatically unless you cancel before the renewal date.</li>
  <li>We may suspend or terminate the Services for non-payment after notice.</li>
  <li>Prices are exclusive of applicable taxes, which are your responsibility.</li>
</ul>

<h4>4. Acceptable Use</h4>
<ul>
  <li>Do not use the Services unlawfully or to infringe the rights of others.</li>
  <li>Do not upload malicious code, attempt to bypass security, or overload the Service.</li>
  <li>Do not send spam or marketing without a lawful basis and valid consent where required.</li>
  <li>Do not collect or store special category data (e.g., health) unless strictly necessary and lawful. If you require such processing, contact us first.</li>
</ul>

<h4>5. Customer Content &amp; Responsibility</h4>
<ul>
  <li>You retain all rights to your content (business details, staff photos, services, customer lists, notes).</li>
  <li>You grant us a non-exclusive licence to host, process and transmit your content solely to provide and improve the Services.</li>
  <li>You are responsible for the accuracy and lawfulness of your content and communications with your customers.</li>
</ul>

<h4>6. Our Intellectual Property</h4>
<ul>
<li>We (and our licensors) retain all rights in the Services, software, documentation, and branding. No rights are granted except as expressly stated in these Terms.</li>
</ul>

<h4>7. Privacy &amp; Data Protection</h4>
<ul>
<li>Data protection is governed by our <strong>Data Processing Addendum (DPA)</strong>, which forms part of these Terms. You are the Controller of your customer data; we are the Processor. You must publish appropriate privacy notices on your booking portal/mini-site and obtain any necessary consents (e.g., for marketing).</li>
</ul>

<h4>8. Cookies &amp; Authentication</h4>
<ul>
<li>The application uses a <em>strictly necessary</em> authentication cookie named <code>Access-Token</code> (a JWT) with an approximate lifetime of 24 hours to protect accounts and sessions. The booking portal does not use analytics cookies by default. Local storage may store non-essential UI preferences (e.g., pagination) on the device.</li>
</ul>

<h4>9. Payments via Stripe</h4>
<ul>
<li>Online payments are processed by <strong>Stripe Connect</strong>. Card details are entered directly into Stripe; we do not store card numbers. Your use of Stripe is subject to Stripe’s own terms and privacy policy.</li>
</ul>

<h4>10. Service Changes &amp; Availability</h4>
<ul>
  <li>We may improve or modify features from time to time. If we deprecate a material feature, we will provide reasonable notice.</li>
  <li>We aim for reliable availability but do not guarantee uninterrupted Service.</li>
</ul>

<h4>11. Support</h4>
<ul>
<li>Support is available via in-app tickets and email at <a href="mailto:${SUPPORT_EMAIL}">${SUPPORT_EMAIL}</a>.</li>
</ul>

<h4>12. Suspension</h4>
<ul
<li>We may suspend the Services for non-payment, security risks, legal compliance, or material breach, with prior notice where reasonable.</li>
</ul>

<h4>13. Term &amp; Termination</h4>
<ul>
  <li>These Terms continue until terminated. Either party may terminate for convenience on 30 days’ notice, or for material breach if uncured within 30 days of notice.</li>
  <li>Upon termination, you may export your data. We will delete personal data in accordance with the DPA (including retention and backup periods).</li>
</ul>

<h4>14. Warranties &amp; Disclaimers</h4>
<ul>
<li>We will provide the Services with reasonable care and skill. Except as set out here, the Services are provided “as is” and we disclaim implied warranties of merchantability, fitness for a particular purpose, and non-infringement to the maximum extent permitted by law.</li>
</ul>

<h4>15. Limitation of Liability</h4>
<ul>
<li>To the maximum extent permitted by law, neither party is liable for indirect, incidental, special or consequential damages, loss of profits or revenues, or loss of data, even if advised of the possibility. Each party’s aggregate liability arising out of or related to the Services in any 12-month period is limited to the fees paid or payable by you to us for the Services in that period. Nothing excludes liability for death or personal injury caused by negligence, fraud, or matters that cannot be excluded by law.</li>
</ul>

<h4>16. Indemnities</h4>
<ul>
  <li><strong>Your indemnity:</strong> You will indemnify us against third-party claims arising from your unlawful use of the Services, your content, or your failure to provide lawful notices/consents.</li>
  <li><strong>Our indemnity:</strong> We will indemnify you against third-party claims alleging that the Services (as provided by us) infringe intellectual property rights, subject to customary exclusions and your prompt notice/co-operation.</li>
</ul>

<h4>17. Confidentiality</h4>
<ul>
<li>Each party will protect the other’s confidential information and use it only for purposes of these Terms.</li>
</ul>

<h4>18. Governing Law &amp; Venue</h4>
<ul>
<li>These Terms are governed by the laws of England &amp; Wales. The courts of England &amp; Wales have exclusive jurisdiction, except that either party may seek injunctive relief in any competent court.</li>
</ul>

<h4>19. Changes to these Terms</h4>
<ul>
<li>We may update these Terms to reflect changes in law or the Services. We will give reasonable prior notice of material changes to the email address of your account administrators or in-app. Continued use after the effective date constitutes acceptance.</li>
</ul>

<h4>20. Force Majeure</h4>
<ul>
<li>Neither party is liable for delays or failures due to events beyond reasonable control (e.g., internet outages, power failures, natural disasters).</li>
</ul>

<h4>21. Notices</h4>
<ul>
<li>Notices to ${COMPANY_NAME}: <a href="mailto:${SUPPORT_EMAIL}">${SUPPORT_EMAIL}</a> and the registered address above. Notices to you: to the account email listed in your account profile.</li>
</ul>

<h4>22. Order of Precedence</h4>
<ul>
<li>If there is any conflict between these Terms and the DPA, the DPA controls for data-protection matters.</li>
</ul>
`;
